/**
 * swa-Style Sticky Add-to-Cart with Modal Variant Selector
 * Standalone implementation with scroll detection, modal management, and cart functionality
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    scrollThreshold: window.innerHeight / 2,
    animationDuration: 300,
    zIndex: {
      sticky: 1000,
      overlay: 9999,
      modal: 10000
    }
  };

  // State management
  const state = {
    isVisible: false,
    isModalOpen: false,
    selectedVariant: null,
    isAddingToCart: false
  };

  // DOM elements cache
  let elements = {};

  /**
   * Initialize the component
   */
  function init() {
    // Cache DOM elements
    elements = {
      stickyBar: document.getElementById('swaStickyATC'),
      selectSizeBtn: document.getElementById('swaSelectSizeBtn'),
      modalOverlay: document.getElementById('swaModalOverlay'),
      modalContainer: document.querySelector('.swa-modal-container'),
      modalClose: document.querySelector('.swa-modal-close'),
      sizeOptions: document.querySelectorAll('.swa-size-option'),
      addToCartBtn: document.getElementById('swaAddToCartBtn'),
      variantForm: document.getElementById('swaVariantForm'),
      variantIdInput: document.getElementById('swaVariantId'),
      priceElements: document.querySelectorAll('.swa-price, .swa-modal-price'),
      comparePriceElements: document.querySelectorAll('.swa-compare-price, .swa-modal-compare-price'),
      btnText: document.querySelectorAll('.swa-btn-text'),
      btnLoading: document.querySelector('.swa-btn-loading')
    };

    // Check if required elements exist
    if (!elements.stickyBar || !elements.modalOverlay) {
      console.warn('swa Sticky ATC: Required elements not found');
      return;
    }

    // Initialize event listeners
    setupEventListeners();
    
    // Set initial state
    elements.addToCartBtn.disabled = true;
    
    // Check if there are any available variants
    const availableVariants = window.swaStickyATC?.variants || [];
    if (availableVariants.length === 0) {
      // Disable the select size button if no variants are available
      if (elements.selectSizeBtn) {
        elements.selectSizeBtn.disabled = true;
        elements.selectSizeBtn.textContent = 'Sold Out';
        elements.selectSizeBtn.classList.add('swa-select-size-btn--disabled');
      }
    }
    
    console.log('swa Sticky ATC initialized');
  }

  /**
   * Setup all event listeners
   */
  function setupEventListeners() {
    // Scroll detection
    let scrollTimeout;
    const handleScroll = () => {
      if (scrollTimeout) return;
      
      scrollTimeout = setTimeout(() => {
        const shouldShow = window.scrollY > CONFIG.scrollThreshold;
        
        if (shouldShow !== state.isVisible) {
          state.isVisible = shouldShow;
          toggleStickyBar(shouldShow);
        }
        
        scrollTimeout = null;
      }, 16); // ~60fps
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Modal controls
    elements.selectSizeBtn?.addEventListener('click', openModal);
    elements.modalClose?.addEventListener('click', closeModal);
    elements.modalOverlay?.addEventListener('click', handleOverlayClick);

    // Size selection
    elements.sizeOptions.forEach(option => {
      option.addEventListener('click', handleSizeSelection);
    });

    // Add to cart
    elements.addToCartBtn?.addEventListener('click', handleAddToCart);

    // Keyboard navigation
    document.addEventListener('keydown', handleKeydown);
  }

  /**
   * Toggle sticky bar visibility
   */
  function toggleStickyBar(show) {
    if (show) {
      elements.stickyBar.classList.remove('hidden');
      elements.stickyBar.classList.add('swa-sticky-bar--visible');
    } else {
      elements.stickyBar.classList.add('hidden');
      elements.stickyBar.classList.remove('swa-sticky-bar--visible');
    }
  }

  /**
   * Open the variant selector modal
   */
  function openModal() {
    if (state.isModalOpen) return;

    state.isModalOpen = true;
    elements.modalOverlay.classList.remove('hidden');
    elements.modalOverlay.classList.add('swa-modal--open');
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    
    // Focus management
    elements.modalContainer.focus();
    
    // Announce to screen readers
    announceToScreenReader('Size selector opened');
  }

  /**
   * Close the variant selector modal
   */
  function closeModal() {
    if (!state.isModalOpen) return;

    state.isModalOpen = false;
    elements.modalOverlay.classList.add('swa-modal--closing');
    
    // Allow body scroll
    document.body.style.overflow = '';
    
    // Remove selected state from size options
    elements.sizeOptions.forEach(option => {
      option.classList.remove('swa-size-option--selected');
    });
    
    // Reset add to cart button
    setAddToCartButtonState('', 'Add to Cart', true);
    state.selectedVariant = null;
    
    // Clean up after animation
    setTimeout(() => {
      elements.modalOverlay.classList.remove('swa-modal--open', 'swa-modal--closing');
      elements.modalOverlay.classList.add('hidden');
    }, CONFIG.animationDuration);
    
    // Announce to screen readers
    announceToScreenReader('Size selector closed');
  }

  /**
   * Handle overlay click (close modal)
   */
  function handleOverlayClick(e) {
    if (e.target === elements.modalOverlay) {
      closeModal();
    }
  }

  /**
   * Handle keyboard navigation
   */
  function handleKeydown(e) {
    if (!state.isModalOpen) return;

    // Close on Escape
    if (e.key === 'Escape') {
      closeModal();
      return;
    }

    // Tab navigation within modal
    if (e.key === 'Tab') {
      const focusableElements = elements.modalOverlay.querySelectorAll(
        'button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  }

  /**
   * Handle size option selection
   */
  function handleSizeSelection(e) {
    const option = e.currentTarget;
    
    if (option.disabled) return;

    // Remove previous selection
    elements.sizeOptions.forEach(opt => {
      opt.classList.remove('swa-size-option--selected');
    });

    // Add selection to clicked option
    option.classList.add('swa-size-option--selected');

    // Update state
    state.selectedVariant = {
      id: option.dataset.variantId,
      title: option.dataset.variantTitle,
      price: option.dataset.variantPrice,
      available: option.dataset.variantAvailable === 'true'
    };

    // Update price display
    updatePriceDisplay(state.selectedVariant);

    // Update add to cart button
    setAddToCartButtonState('', 'Add to Cart', false);

    // Update variant form
    elements.variantIdInput.value = state.selectedVariant.id;

    // Announce selection
    announceToScreenReader(`Selected size: ${state.selectedVariant.title}`);
  }

  /**
   * Update price display across all price elements
   */
  function updatePriceDisplay(variant) {
    if (!variant) return;

    // Update price
    elements.priceElements.forEach(el => {
      el.textContent = variant.price;
    });

    // Update compare at price if available
    const productData = window.swaStickyATC?.formatted?.[variant.id];
    if (productData?.compareAtPrice) {
      elements.comparePriceElements.forEach(el => {
        el.textContent = productData.compareAtPrice;
        el.style.display = 'inline';
      });
    } else {
      elements.comparePriceElements.forEach(el => {
        el.style.display = 'none';
      });
    }
  }

  /**
   * Set add to cart button state
   */
  function setAddToCartButtonState(state, text, disabled) {
    elements.addToCartBtn.disabled = disabled;
    elements.addToCartBtn.className = `swa-add-to-cart-btn ${state}`;
    
    if (state === 'loading') {
      elements.btnText.forEach(el => el.classList.add('hidden'));
      elements.btnLoading.classList.remove('hidden');
    } else {
      elements.btnText.forEach(el => {
        el.classList.remove('hidden');
        if (text) el.textContent = text;
      });
      elements.btnLoading.classList.add('hidden');
    }
  }

  /**
   * Handle add to cart action
   */
  async function handleAddToCart() {
    if (!state.selectedVariant || state.isAddingToCart) return;

    state.isAddingToCart = true;
    setAddToCartButtonState('loading', '', true);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: state.selectedVariant.id,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Item added to cart:', data);

      // Success state
      setAddToCartButtonState('success', 'Added!', true);
      
      // Close modal after short delay
      setTimeout(() => {
        closeModal();
        announceToScreenReader('Item added to cart successfully');
      }, 1500);

      // Dispatch custom event for other components
      document.dispatchEvent(new CustomEvent('swa:cart:add', {
        detail: { variant: state.selectedVariant, response: data }
      }));

    } catch (error) {
      console.error('Error adding item to cart:', error);
      
      // Error state
      setAddToCartButtonState('error', 'Try Again', false);
      
      // Reset after delay
      setTimeout(() => {
        setAddToCartButtonState('', 'Add to Cart', false);
        state.isAddingToCart = false;
      }, 3000);

      // Announce error
      announceToScreenReader('Failed to add item to cart. Please try again.');
    }
  }

  /**
   * Announce messages to screen readers
   */
  function announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  /**
   * Cleanup function
   */
  function cleanup() {
    window.removeEventListener('scroll', handleScroll);
    document.removeEventListener('keydown', handleKeydown);
    
    if (elements.selectSizeBtn) {
      elements.selectSizeBtn.removeEventListener('click', openModal);
    }
    if (elements.modalClose) {
      elements.modalClose.removeEventListener('click', closeModal);
    }
    if (elements.modalOverlay) {
      elements.modalOverlay.removeEventListener('click', handleOverlayClick);
    }
    if (elements.addToCartBtn) {
      elements.addToCartBtn.removeEventListener('click', handleAddToCart);
    }
    
    elements.sizeOptions.forEach(option => {
      option.removeEventListener('click', handleSizeSelection);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose cleanup for manual use
  window.swaStickyATCCleanup = cleanup;

})();
